import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import SEO from '../components/SEO';
import './TermsOfServicePage.css';

// To update this page, edit the `sections` array below.
// Each section has a `title` and a `content` array.
// Strings render as paragraphs. { type: 'list', items: [...] } renders as a bullet list.
const LAST_UPDATED = 'April 2026';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: [
      'By accessing or using DiveConnect ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Platform.',
      'We reserve the right to update these terms at any time. Continued use of the Platform after changes are posted constitutes acceptance of the revised terms.',
    ],
  },
  {
    title: '2. Eligibility',
    content: [
      'You must be at least 18 years old to create an account and book trips. Users aged 12–17 may participate in designated junior programmes only with verifiable parental or guardian consent.',
      'By registering, you confirm that the information you provide is accurate and that you are legally permitted to enter into this agreement.',
    ],
  },
  {
    title: '3. Your Account',
    content: [
      'You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.',
      'You must notify us immediately at info@dive-connect.com if you suspect unauthorised access to your account.',
      'We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or are inactive for an extended period.',
    ],
  },
  {
    title: '4. Booking & Payments',
    content: [
      'Submitting a trip application does not guarantee a confirmed booking. A booking is confirmed only when you receive a written confirmation from DiveConnect.',
      'All prices listed are in the currency stated on the trip page. Prices are subject to change until a booking is confirmed.',
      'Payments are processed securely by our third-party payment provider. DiveConnect does not store your card details.',
    ],
  },
  {
    title: '5. Cancellations & Refunds',
    content: [
      'The following cancellation policy applies unless stated otherwise on the trip page:',
      {
        type: 'list',
        items: [
          'Cancellation 30+ days before departure: full refund',
          'Cancellation 14–30 days before departure: 50% refund',
          'Cancellation within 14 days of departure: no refund',
        ],
      },
      'You may transfer your spot to another eligible diver at no charge with at least 7 days\' notice. Please contact us to arrange this.',
      'DiveConnect reserves the right to cancel a trip due to insufficient bookings, severe weather, or safety concerns. In such cases, a full refund will be issued.',
    ],
  },
  {
    title: '6. Health, Safety & Certifications',
    content: [
      'You are responsible for ensuring you hold the required diving certification for any trip you book. Falsifying certification details may result in removal from the trip without refund.',
      'You confirm that you are in good physical health and aware of the risks associated with scuba diving. Certain medical conditions require a doctor\'s clearance before diving.',
      'We strongly recommend holding valid dive insurance (e.g. DAN — Divers Alert Network) for all trips. Some trips require it as a condition of participation.',
      'DiveConnect and its trip operators are not liable for injuries, illness, or death arising from participation in diving activities, except where caused by our own proven negligence.',
    ],
  },
  {
    title: '7. User Conduct',
    content: [
      'You agree not to use the Platform to:',
      {
        type: 'list',
        items: [
          'Post false, misleading, or fraudulent information',
          'Harass, threaten, or harm other users',
          'Attempt to gain unauthorised access to any part of the Platform',
          'Scrape, copy, or redistribute content without permission',
          'Violate any applicable laws or regulations',
        ],
      },
      'Violations may result in immediate account suspension and, where applicable, legal action.',
    ],
  },
  {
    title: '8. Intellectual Property',
    content: [
      'All content on DiveConnect — including text, images, logos, and code — is the property of DiveConnect or its licensors and is protected by applicable copyright and trademark laws.',
      'You may not reproduce, distribute, or create derivative works from any content on the Platform without our prior written consent.',
    ],
  },
  {
    title: '9. Third-Party Links & Services',
    content: [
      'The Platform may contain links to third-party websites or services. We are not responsible for the content, privacy practices, or reliability of any third-party service.',
      'Use of third-party services is at your own risk and subject to their own terms and conditions.',
    ],
  },
  {
    title: '10. Limitation of Liability',
    content: [
      'To the fullest extent permitted by law, DiveConnect shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform or participation in any trip.',
      'Our total liability to you in connection with any claim shall not exceed the amount you paid for the specific trip giving rise to the claim.',
    ],
  },
  {
    title: '11. Governing Law',
    content: [
      'These Terms of Service are governed by and construed in accordance with applicable law. Any disputes shall be resolved in the courts of the jurisdiction in which DiveConnect is registered.',
    ],
  },
  {
    title: '12. Contact',
    content: [
      'If you have questions about these Terms of Service, please contact us at info@dive-connect.com.',
    ],
  },
];

const TermsOfServicePage = () => {
  return (
    <div className="terms-page">
      <SEO
        title="Terms of Service - DiveConnect"
        description="Read the DiveConnect Terms of Service to understand your rights and responsibilities when using our platform."
      />

      {/* Hero */}
      <div className="terms-hero">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="terms-hero-title">Terms of Service</h1>
              <p className="terms-hero-subtitle">Last updated: {LAST_UPDATED}</p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Content */}
      <section className="terms-content">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <p className="terms-intro">
                Please read these Terms of Service carefully before using DiveConnect.
                They set out the rules for using our platform and booking diving trips.
                By using DiveConnect, you agree to these terms.
              </p>

              {sections.map((section) => (
                <div key={section.title} className="terms-section">
                  <h2 className="terms-section-title">{section.title}</h2>
                  {section.content.map((block, i) =>
                    typeof block === 'string' ? (
                      <p key={i} className="terms-text">{block}</p>
                    ) : (
                      <ul key={i} className="terms-list">
                        {block.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
